import React from 'react';
import TestResultDisplay from '../../common/TestResultDisplay';

// 测试项目接口
interface TestItem {
  name: string;
  passed: boolean;
  userAnswer?: string;
  correctAnswer?: string;
  details?: string;
}

// 测试组接口
interface TestGroup {
  title: string;
  subtitle?: string;
  score?: { current: number; total: number };
  items: TestItem[];
  passed: boolean;
  note?: string;
  hasLogicalError?: boolean;
  errorReason?: string;
}

interface HearingSummaryDisplayProps {
  responses: any;
  hearingStatus?: string;  // 新增：听力评估状态
  className?: string;
}

const HearingSummaryDisplay: React.FC<HearingSummaryDisplayProps> = ({ 
  responses, 
  hearingStatus,
  className = '' 
}) => {

  // 判断听力是否通过 - 基于传入的状态或responses中的filterQuestion
  const hearingPassed = hearingStatus === 'PASS' || responses?.filterQuestion === true;
  
  // 从真实的responses数据中获取听力评估数据
  const getRealHearingData = () => {
    
    // 方式1: 从直接字段获取 - 优先检查filterQuestionResult字段
    if (responses?.filterQuestionResult !== undefined) {
      
      // 🎯 修复：如果直接字段中没有耳语测试数据，尝试从testResults获取
      const combinedData: any = {
        filterQuestion: responses.filterQuestionResult,
        leftEarHeard: responses.leftEarHeard,
        rightEarHeard: responses.rightEarHeard,
        backgroundNoiseFilter: responses.backgroundNoiseFilter,
        conversationDifficultyFilter: responses.conversationDifficultyFilter
      };
      
      // 如果耳语测试数据缺失，从testResults中补充获取
      if ((combinedData.leftEarHeard === undefined || combinedData.rightEarHeard === undefined) && responses?.testResults) {
        const hearingTests = responses.testResults.filter((test: any) => 
          test.name === "耳语测试" || test.name === "左耳耳语测试" || test.name === "右耳耳语测试"
        );
        
        hearingTests.forEach((test: any) => {
          if (test.rawUserAnswers) {
            Object.assign(combinedData, test.rawUserAnswers);
          }
        });
        
      }
      
      return combinedData;
    }
    
    // 方式1.1: 从filterQuestion字段获取（备用）
    if (responses?.filterQuestion !== undefined) {
      return {
        filterQuestion: responses.filterQuestion,
        leftEarHeard: responses.leftEarHeard,
        rightEarHeard: responses.rightEarHeard,
        backgroundNoiseFilter: responses.backgroundNoiseFilter,
        conversationDifficultyFilter: responses.conversationDifficultyFilter
      };
    }
    
    // 方式2: 从testResults中获取rawUserAnswers
    if (responses?.testResults) {
      const hearingTests = responses.testResults.filter((test: any) => 
        test.name === "听力问题筛查" || test.name === "耳语测试" || 
        test.name === "听力功能评估" || test.name === "听力自我评估" ||
        test.name === "左耳耳语测试" || test.name === "右耳耳语测试"
      );
      
      if (hearingTests.length > 0) {
        // 合并所有听力测试的rawUserAnswers数据
        const combinedData: any = {};
        hearingTests.forEach((test: any) => {
          if (test.rawUserAnswers) {
            Object.assign(combinedData, test.rawUserAnswers);
          }
          
          // 🎯 修复：不再从测试结果推断filterQuestion，而是从rawUserAnswers中获取真实用户回答
          if (test.name === "听力问题筛查" && test.rawUserAnswers) {
            // 从rawUserAnswers中获取用户的真实回答
            if (test.rawUserAnswers.filterQuestion !== undefined) {
              combinedData.filterQuestion = test.rawUserAnswers.filterQuestion;
            }
          }
        });
        
        if (Object.keys(combinedData).length > 0) {
          return combinedData;
        }
      }
    }
    
    // 方式3: 从domainResult中的testResults获取
    if ((responses as any)?.status && (responses as any)?.testResults) {
      const hearingTests = (responses as any).testResults.filter((test: any) => 
        test.name === "听力问题筛查" || test.name === "耳语测试" || 
        test.name === "听力功能评估" || test.name === "听力自我评估" ||
        test.name === "左耳耳语测试" || test.name === "右耳耳语测试"
      );
      
      if (hearingTests.length > 0) {
        const combinedData: any = {};
        hearingTests.forEach((test: any) => {
          if (test.rawUserAnswers) {
            Object.assign(combinedData, test.rawUserAnswers);
          }
          
          // 🎯 修复：不再从测试结果推断filterQuestion，而是从rawUserAnswers中获取真实用户回答
          if (test.name === "听力问题筛查" && test.rawUserAnswers) {
            // 从rawUserAnswers中获取用户的真实回答
            if (test.rawUserAnswers.filterQuestion !== undefined) {
              combinedData.filterQuestion = test.rawUserAnswers.filterQuestion;
            }
          }
        });
        
        if (Object.keys(combinedData).length > 0) {
          return combinedData;
        }
      }
    }
    
    
    // 🎯 特殊推断逻辑：按WHO ICOPE标准，有耳语测试说明筛查问题必须通过
    if (responses?.testResults) {
      const hasWhisperTest = responses.testResults.some((test: any) => 
        test.name === "左耳耳语测试" || test.name === "右耳耳语测试" || test.name === "耳语测试"
      );
      
      if (hasWhisperTest) {
        // 构造筛查通过的数据
        const whisperTests = responses.testResults.filter((test: any) => 
          test.name === "左耳耳语测试" || test.name === "右耳耳语测试" || test.name === "耳语测试"
        );
        
        const combinedData: any = {
          filterQuestion: false  // 推断筛查通过
        };
        
        // 提取耳语测试数据
        whisperTests.forEach((test: any) => {
          if (test.rawUserAnswers) {
            Object.assign(combinedData, test.rawUserAnswers);
          }
        });
        
        return combinedData;
      }
    }
    
    return null;
  };
  
  // 检查筛查问题状态
  const realData = getRealHearingData();
  
  // 🔍 调试：检查听力数据结构
  if (realData) {
  }
  
  // 🎯 修复：正确判断筛查问题结果
  // filterQuestionResult: false = 筛查未通过（有听力困难）
  // filterQuestionResult: true = 筛查通过（无听力困难）
  const filterQuestionPassed = realData?.filterQuestion === false || responses?.filterQuestionResult === true;
  const isFilterQuestionFailed = !filterQuestionPassed;
  
  

  // 完整的听力测试 - 基于真实数据生成
  const generateHearingTestData = (): TestGroup[] => {
    
    if (!realData) {
      return [
        {
          title: '听力状况评估',
          passed: hearingPassed,
          items: [
            {
              name: '评估状态',
              passed: hearingPassed,
              userAnswer: hearingPassed ? '听力状况正常' : '听力状况需要关注',
              details: '数据不可用，基于系统评估结果'
            }
          ]
        }
      ];
    }

    const testGroups: TestGroup[] = [];
    // 耳语测试 - 基于真实数据，只有筛查通过时才会有耳语测试
    // 按照WHO ICOPE标准：筛查问题未通过就不进行耳语测试
    if (realData.leftEarHeard !== undefined || realData.rightEarHeard !== undefined) {
      const leftEarPassed = realData.leftEarHeard === true;
      const rightEarPassed = realData.rightEarHeard === true;
      const whisperPassed = leftEarPassed && rightEarPassed;
      
      const items: any[] = [];
      if (realData.leftEarHeard !== undefined) {
        items.push({
          name: '左耳',
          passed: leftEarPassed,
          userAnswer: leftEarPassed ? '能正确重复词语' : '重复有困难',
        });
      }
      if (realData.rightEarHeard !== undefined) {
        items.push({
          name: '右耳',
          passed: rightEarPassed,
          userAnswer: rightEarPassed ? '能正确重复词语' : '重复有困难',
        });
      }
      
      if (items.length > 0) {
        testGroups.push({
          title: '耳语测试',
          score: { current: items.filter(item => item.passed).length, total: items.length },
          passed: whisperPassed,
          items: items
        });
      }
    }

    // 听力功能评估 - 基于真实数据
    if (realData.backgroundNoiseFilter !== undefined || realData.conversationDifficultyFilter !== undefined) {
      const items: any[] = [];
      
      if (realData.backgroundNoiseFilter !== undefined) {
        const backgroundNoisePassed = realData.backgroundNoiseFilter === true;
        items.push({
          name: '背景噪音环境',
          passed: backgroundNoisePassed,
          userAnswer: backgroundNoisePassed ? '无困难' : '有困难',
          details: '用户回答：在噪音环境中听对话的困难程度'
        });
      }
      
      if (realData.conversationDifficultyFilter !== undefined) {
        const conversationPassed = realData.conversationDifficultyFilter === true;
        items.push({
          name: '对话困难程度',
          passed: conversationPassed,
          userAnswer: conversationPassed ? '无困难' : '有困难',
          details: '用户回答：日常对话交流的困难程度'
        });
      }
      
      if (items.length > 0) {
        const allFunctionsPassed = items.every(item => item.passed);
        testGroups.push({
          title: '听力功能评估',
          passed: allFunctionsPassed,
          items: items
        });
      }
    }

    // 听力自我评估 - 基于真实数据
    if (realData.overallHearing !== undefined) {
      const overallHearingPassed = realData.overallHearing === 'good';
      testGroups.push({
        title: '听力自我评估',
        passed: overallHearingPassed,
        items: [
          {
            name: '总体听力状况',
            passed: overallHearingPassed,
            userAnswer: overallHearingPassed ? '听力状况良好' : '听力有困难',
            details: '用户回答：在面对面交谈、安静环境对话、听电视广播、接听电话等情况下的听力表现'
          }
        ]
      });
    }

    // 如果没有任何真实测试数据，显示状态信息
    if (testGroups.length === 0) {
      testGroups.push({
        title: '听力状况评估',
        passed: hearingPassed,
        items: [
          {
            name: '评估结果',
            passed: hearingPassed,
            userAnswer: hearingPassed ? '听力状况正常' : '听力状况需要关注',
            details: '基于WHO ICOPE听力评估标准'
          }
        ]
      });
    }

    return testGroups;
  };

  const testGroups = generateHearingTestData();

  return (
    <TestResultDisplay
      title="听力评估结果"
      subtitle="完整听力功能测试"
      testGroups={testGroups}
      className={className}
    />
  );
};

export default HearingSummaryDisplay;